import { Injectable } from '@angular/core';

/**
 * Registry that maps open story GUIDs to a closure that returns their latest editor text.
 *
 * ## Why this service exists
 *
 * The NgRx store's `text` field on a story record is only updated when the user explicitly
 * saves. Between saves, every keystroke is tracked only in local component state:
 *  - `StoryFullScreenComponent.latestEditorContent` (signal, last `contentChanged` emission)
 *  - `StoryEditorComponent.getCurrentContent()` (reads live from the Froala DOM)
 *
 * When a MOS plugin issues an `ncsStoryRequest`, the `requestRecord$` effect in
 * `PlaybookEffects` must reply with the *current* story body, not the last-saved one.
 * NgRx effects cannot reach into Angular component instances directly, so this service
 * acts as a lightweight bridge: components register a getter while they are alive, and the
 * effect calls that getter on demand.
 *
 * ## Lifecycle contract
 *
 * | Who          | When                                  | What                                         |
 * |--------------|---------------------------------------|----------------------------------------------|
 * | `StoryFullScreenComponent` | Story Guid changes (ngOnInit effect) | Unregisters old Guid, registers new Guid |
 * | `StoryFullScreenComponent` | `ngOnDestroy`                        | Unregisters current Guid                     |
 * | `PlaybookEffects`          | `requestRecord$` (MOS ncsStoryRequest) | Calls `getLatestText(guid)`, merges result into record before MOS response |
 *
 * ## Getter priority (implemented in StoryFullScreenComponent)
 *
 * 1. `richTextEditor()?.getCurrentContent()` — live read from Froala DOM; most up-to-date,
 *    even ahead of the collaboration-editor's 300 ms debounce.
 * 2. `latestEditorContent()` — last emission captured by the `(contentChanged)` output;
 *    used when the editor ViewChild is not yet in the DOM.
 * 3. `data()?.text` — last value written to the NgRx store (i.e. last explicit save);
 *    final fallback when the component has just been created and the editor has not emitted.
 */
@Injectable({
    providedIn: 'root',
})
export class StoryEditorTextService {
    private readonly textGetters = new Map<string, () => string>();

    /**
     * Associates `storyGuid` with a closure that, when called, returns the story's
     * current editor text as a single HTML string (above + black-line separator + below).
     *
     * Calling this with a Guid that already has an entry silently replaces the old getter —
     * which is the expected behaviour when a sidecar switches from one story to another
     * sharing the same open-editor slot.
     */
    registerTextGetter(storyGuid: string, getter: () => string): void {
        this.textGetters.set(storyGuid, getter);
    }

    /**
     * Removes the getter for `storyGuid`.  Should be called:
     *  - Before registering a new story on the same editor slot (story switch).
     *  - In `ngOnDestroy` of the hosting component.
     *
     * Safe to call with a Guid that was never registered (no-op).
     */
    unregisterTextGetter(storyGuid: string): void {
        this.textGetters.delete(storyGuid);
    }

    /**
     * Returns the latest editor text for `storyGuid` by invoking the registered getter,
     * or `null` when no editor is currently open for that story (e.g. the story is not
     * loaded in any sidecar or fullscreen slot).
     *
     * Callers that receive `null` should fall back to the NgRx store value, which
     * represents the last explicitly saved text.
     */
    getLatestText(storyGuid: string): string | null {
        const getter = this.textGetters.get(storyGuid);
        return getter ? getter() : null;
    }
}
